import { ColumnIconType } from "@components/new/Table/useTableSort";
import { iTableConfigItem } from "@components/new/Table/Table.helpers";
import { FC, useCallback, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { routeNames } from "@core/routeNames";
import Button from "@components/new/Button/Button";
import { components } from "@apiSchema";
import { iChainMenuItems } from "@apiHooks/useChainsMenuItems";
import { highlightTerms } from "@helpers";

type iMenuItemProps = components['schemas']['MenuItemViewModel'] & {
    menuIncludes?: string[];
};

const MenuItem: FC<iMenuItemProps> = (
    {
        title,
        description,
        menuIncludes,
    }
) => {
    const toHtml = (text: string) => menuIncludes ? highlightTerms(text, menuIncludes, true) : text;

    return (
        <div className="WhoOffersMenuItem">
            <div
                className="WhoOffersMenuItem__title"
                dangerouslySetInnerHTML={{
                    __html: toHtml(title ?? ''),
                }}
            />
            {description && (
                <div
                    className="WhoOffersMenuItem__description"
                    dangerouslySetInnerHTML={{
                        __html: toHtml(description)
                    }}
                />
            )}
        </div>
    );
};

export function useTableConfig(
    data: iChainMenuItems[],
    sortingField: keyof iChainMenuItems | null,
    sortingDirection: 'asc' | 'desc',
    handleSort: (fieldName: keyof iChainMenuItems) => () => void,
    getColumnIcon: ColumnIconType,
    expandedRowIndexes: number[],
    setExpandedRowIndexes: (newValue: number[]) => void,
    menuIncludes?: string[],
): iTableConfigItem[] {
    const columnIcon = useCallback(
        (fieldName: keyof iChainMenuItems) => getColumnIcon(fieldName, sortingField, sortingDirection),
        [getColumnIcon, sortingDirection, sortingField]
    );

    useEffect(() => {
        setExpandedRowIndexes([]);
    }, [data, sortingField, sortingDirection, setExpandedRowIndexes]);

    return useMemo((): iTableConfigItem[] => [
        {
            width: 300,
            title: 'Chain',
            icon: columnIcon('chainName'),
            isActive: sortingField === 'chainName',
            styles: {
                cell: {
                    position: 'sticky',
                    top: 0,
                    bottom: 0,
                    minHeight: 0,
                }
            },
            Value: ({ index }) => (
                <Link
                    className="NewTopChains__link"
                    to={{
                        pathname: routeNames.chainDetails,
                        search: `id=${data[index].chainId}`,
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {data[index].chainName}
                </Link>
            ),
            handleHeaderClick: handleSort('chainName'),
        },
        {
            title: 'Menu items',
            styles: { cell: { height: 'auto', minHeight: 40 } },
            Value: ({ index }) => (
                <div className="WhoOffers__menu-items">
                    {!expandedRowIndexes.includes(index) && (
                        <MenuItem
                            {...data[index].menuItems?.[0]}
                            menuIncludes={menuIncludes}
                        />
                    )}
                    {expandedRowIndexes.includes(index) && data[index].menuItems?.map((i, iIndex) => (
                        <MenuItem
                            key={`${data[index].chainId}-${iIndex}`}
                            {...i}
                            menuIncludes={menuIncludes}
                        />
                    ))}
                </div>
            )
        },
        {
            width: 130,
            title: '',
            Value: ({ index }) => (
                <>
                    {(data[index].menuItems?.length ?? 0) > 1 && (
                        <>
                            {expandedRowIndexes.includes(index)
                                ? (
                                    <Button
                                        modifiers={["secondary"]}
                                        onClick={() => setExpandedRowIndexes([...expandedRowIndexes.filter(i => i !== index)])}
                                        testId={`buttonFoodsViewLess-${index}`}
                                    >
                                        View less
                                    </Button>
                                )
                                : (
                                    <Button
                                        modifiers={["secondary"]}
                                        onClick={() => setExpandedRowIndexes([...expandedRowIndexes, index])}
                                        testId={`buttonFoodsViewMore-${index}`}
                                    >
                                        plus {(data[index].menuItems?.length ?? 0) - 1} more
                                    </Button>
                                )}
                        </>
                    )}
                </>
            )
        }
    ], [columnIcon, handleSort, data, sortingField, expandedRowIndexes, setExpandedRowIndexes, menuIncludes])
}